import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Divider, Grid, IconButton, InputAdornment, TextareaAutosize, TextField } from "@mui/material";
import { GridCloseIcon, GridExpandMoreIcon } from "@mui/x-data-grid";

import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import DataObjectIcon from '@mui/icons-material/DataObject';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import { Delete } from "@mui/icons-material";

import globalStyles from "../../../../../assets/styles/global.module.scss";
import styles from "../../../../../assets/styles/componentStyles/templateLibraryStyles/modifyWhatsAppTemplate/whatsAppTemplateForm.module.scss";

import { useShallow } from "zustand/react/shallow";
import { CAROUSEL_MAX_CHARS, displayFlexCursorPointerClassName, doesCarouselMessageBodyTextStartOrEndWithVariable, renderBodyVariableEdgeWarning, renderTooManyVariableParameterWarning, VARIABLE_REGEX } from "../../../channels/global/whatsapp/templates/utils";
import { useTemplateLibraryStore } from "../../../../../store/templateLibraryStore";
import { useCarouselFunctions } from "../../utils/useCarouselFunctions";
import { useInsertVariableAtCursorPosition } from "../../../../../hooks/useInsertVariableAtCursorPosition";
import { handleKeyDownForFormatting, hasConsecutiveNewlinesInCarouselBody, isTemplateCreated } from "../../constant";
import Required from "../../../../utility/required";
import EmojiPickerButton from "../../utils/EmojiPickerButton";
import insertTextAtCursor from "../../utils/insertTextAtCursor";
import TouchTooltip from "../../utils/TouchTooltip";
import VariableDisclaimer from "../../utils/VariableDisclaimer";
import SampleVariableKeyLabel from "../../utils/SampleVariableKeyLabel";

const CarouselFormBodyText = (props: any) => {

    const { handleChangeText } = props;

    const { createTemplatePayload } = useTemplateLibraryStore(useShallow((state) => ({
        createTemplatePayload: state.createTemplatePayload,
    })));

    const { headerTextareaCarouselRef, handleFormattingCarousel, checkFlagErrorCarousel, handleAddCarouselField, 
        checkDisableAddVarButtonCarousel, handleChangeCarouselVar, handleRemoveCarouselField, updateCarouselMessageBodyText 
    } = useCarouselFunctions();

    const { insertVariable, registerTextarea } = useInsertVariableAtCursorPosition();

    const carouselMessageBodyText = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header ?? "";
    const carouselMessageBodyLength = carouselMessageBodyText.replace(VARIABLE_REGEX["FLAG_NUMBER"], "*").length;
    const showConsecutiveNewlinesError = hasConsecutiveNewlinesInCarouselBody(carouselMessageBodyText);

    const handleCarouselBodyTextChange = (value: string, cursorPosition?: number) => {
        updateCarouselMessageBodyText(value, handleChangeText, cursorPosition);
    };

    const handleInsertEmoji = (emoji: string, selection: { start: number; end: number } | null) => {
        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        const headerValue = latestPayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header ?? "";
        const start = selection?.start ?? headerValue.length;
        const end = selection?.end ?? headerValue.length;
        const result = insertTextAtCursor(headerValue, emoji, start, end);

        handleCarouselBodyTextChange(result.value, result.cursorPosition);
    };

    const isTemplateFormattingDisabled = isTemplateCreated(createTemplatePayload);
    const formattingIconClassName = `${displayFlexCursorPointerClassName} ${styles.formattingIcon} ${isTemplateFormattingDisabled ? styles.disabledIcon : ''}`;

    return (
        <Accordion defaultExpanded={true} className={`${styles.accordion}`}>
            
            <AccordionSummary expandIcon={<GridExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header" className={styles.accordion__summary}>
                <Grid container justifyContent={"space-between"} alignItems={"center"}>
                    <span className={styles.accordion__summary__text}>
                       Message Body Text <Required />
                    </span>
                    
                    <span className={styles.accordion__summary__value}>
                        {`${carouselMessageBodyLength}/${CAROUSEL_MAX_CHARS?.HEADER_TEXT}`}
                    </span>
                </Grid>
            </AccordionSummary>

            <Divider sx={{ width: "100%", opacity: "1" }} />

            <AccordionDetails className={styles.accordion__details}>
                <Grid container xs={12}>

                    <Grid item xs={12} className={styles.whatsappTemplateFormBody__bodyTextContainer}>

                        {/* Formatting */}
                        <Grid container className={styles.formattingContainer}>

                            {/* Formatting Buttons */}
                            <Grid item sx={{ width: "auto", flexShrink: 0 }}>
                                <Box className={styles.formattingToolbarGroup}>
                                    
                                    <Box className={styles.formattingToolbarIcons}>
                                        <TouchTooltip title={"Bold (Ctrl + B)"}>
                                            <FormatBoldIcon
                                                className={formattingIconClassName}
                                                onClick={() => handleFormattingCarousel('bold', 'header')}
                                            />
                                        </TouchTooltip>

                                        <TouchTooltip title={"Italic (Ctrl + I)"}>
                                            <FormatItalicIcon
                                                className={formattingIconClassName}
                                                onClick={() => handleFormattingCarousel('italic', 'header')}
                                            />
                                        </TouchTooltip>

                                        <TouchTooltip title={"Strikethrough (Ctrl + S)"}>
                                            <StrikethroughSIcon
                                                className={formattingIconClassName}
                                                onClick={() => handleFormattingCarousel('strikethrough', 'header')}
                                            />
                                        </TouchTooltip>
                                    </Box>

                                    <Divider orientation="vertical" flexItem className={styles.formattingToolbarDivider} />

                                    <EmojiPickerButton
                                        textareaRef={headerTextareaCarouselRef}
                                        disabled={Boolean(createTemplatePayload?.whatsapp_template_id)}
                                        className={styles.formattingIcon}
                                        onSelectEmoji={handleInsertEmoji}
                                    />
                                </Box>
                            </Grid>

                            {/* Add Variable Button */}
                            <Grid item className={styles.addVariableButtonContainer} sx={{ width: "auto" }}>
                                <Button
                                    variant="outlined"
                                    size='small'
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleAddCarouselField("header", insertVariable)}
                                    className={`${styles.callToActionButton} ${globalStyles.clickable}`}
                                    disabled={createTemplatePayload.whatsapp_template_id ? true : checkDisableAddVarButtonCarousel()}
                                    sx={{
                                        ...(checkDisableAddVarButtonCarousel() && {
                                            opacity: "0.5"
                                        }),
                                        visibility: isTemplateCreated(createTemplatePayload) ? "hidden" : "visible",
                                    }}
                                    startIcon={<DataObjectIcon />}
                                >
                                    Add Variable
                                </Button>
                            </Grid>
                            
                        </Grid>

                        {/* Body Textarea */}
                        <TextareaAutosize
                            disabled={createTemplatePayload.whatsapp_template_id ? true : false}
                            ref={(el: HTMLTextAreaElement | null) => {
                                headerTextareaCarouselRef.current = el;
                                registerTextarea('header', el);
                            }}
                            className={styles.whatsappTemplateFormBody__bodyTextArea}
                            value={carouselMessageBodyText}
                            onChange={(event: any) => handleCarouselBodyTextChange(event.target.value)}
                            onKeyDown={(e) => handleKeyDownForFormatting(e, handleFormattingCarousel, "header")}
                            autoComplete='off'
                            placeholder='Enter Message Body Text'
                            dir="auto"
                        />

                    </Grid>

                    {/* Too Many Variable Warning */}
                    {checkFlagErrorCarousel("header") &&
                        renderTooManyVariableParameterWarning()
                    }

                    {/* Consecutive Newlines Error */}
                    {showConsecutiveNewlinesError && (
                        <Grid item xs={12} className={globalStyles.marginTop10px}>
                            <VariableDisclaimer text="Paragraphs are not supported in the body." />
                        </Grid>
                    )}

                    {/* Body Text Start or End with Variable Warning */}
                    {doesCarouselMessageBodyTextStartOrEndWithVariable(createTemplatePayload) &&
                        renderBodyVariableEdgeWarning()
                    }

                    {/* Body Text Dynamic Variables */}
                    {createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable &&
                        createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable.length > 0 &&
                        <Grid item xs={12} className={styles.marginTop5px}>
                            {createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable?.map((field: any, ind: number) => (
                                <React.Fragment key={ind}>
                                    
                                    <Grid item xs={12} sx={{ margin: '10px 0px' }}>
                                        <Grid container sx={{ alignItems: 'center' }} flexWrap={"nowrap"}>
                                            
                                            <Grid item className={styles.sampleVariableKeyColumn}>
                                                <SampleVariableKeyLabel
                                                    variableKey={`${field?.whatsapp_template_dynamic_variable_key}`}
                                                    className={styles.sampleVariableKeyText}
                                                />
                                            </Grid>

                                            <Grid item xs={9} key={field?.whatsapp_template_dynamic_variable_id}>
                                                <TextField
                                                    disabled={isTemplateCreated(createTemplatePayload)}
                                                    size='small'
                                                    variant="outlined"
                                                    className={styles.sampleVariableTextField}
                                                    // value={field.value}
                                                    onChange={(e) => handleChangeCarouselVar(field?.whatsapp_template_dynamic_variable_key, e, "header")}
                                                    fullWidth
                                                    autoComplete='off'
                                                    sx={{
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            border: 'none'
                                                        }
                                                    }}
                                                    value={field?.whatsapp_template_dynamic_variable_sample_value}
                                                    InputProps={{
                                                        endAdornment: (!createTemplatePayload?.whatsapp_template_id && field?.whatsapp_template_dynamic_variable_sample_value?.trim() !== '') ? (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    size="small"
                                                                    edge="end"
                                                                    aria-label="Clear sample text"
                                                                    onClick={() => handleChangeCarouselVar(field?.whatsapp_template_dynamic_variable_key, { target: { value: '' } }, "header")}
                                                                    className={globalStyles.clickable}
                                                                >
                                                                    <GridCloseIcon className={styles.sampleVariableClearIcon} />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ) : null,
                                                    }}
                                                />
                                            </Grid>

                                            {!isTemplateCreated(createTemplatePayload) && (
                                                <Grid item sx={{ marginLeft: '5px' }}>
                                                    <IconButton
                                                        onClick={() => handleRemoveCarouselField(field?.whatsapp_template_dynamic_variable_key, "header")}
                                                        disabled={isTemplateCreated(createTemplatePayload)}
                                                        className={styles.sampleVariableDeleteButton}
                                                    >
                                                        <Delete className={styles.sampleVariableDeleteIcon} />
                                                    </IconButton>
                                                </Grid>
                                            )}

                                        </Grid>
                                    </Grid>

                                </React.Fragment>
                            ))}
                        </Grid>
                    }

                    {/* Add Sample Variable Text */}
                    {createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable &&
                        createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable?.filter((data: any) => data.whatsapp_template_dynamic_variable_sample_value.trim() === "").length !== 0 &&
                        
                        <Grid item marginBlock={"12px"} xs={12}>
                            <VariableDisclaimer text="Add sample text" />
                        </Grid>
                    }

                </Grid>
            </AccordionDetails>
        </Accordion>
    )

}

export default CarouselFormBodyText;
